import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import HourglassEmptyIcon from '@material-design-icons/svg/filled/hourglass_empty.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import { useEffect, useState } from 'react';
import { getStatusConfig } from '../../constants/statusConfig';

interface SyncStatusBadgeProps {
  status: string | null;
  lastSyncTimestamp: Date | null;
  errorMessage?: string | null;
  provider: string;
}

export function SyncStatusBadge({
  status,
  lastSyncTimestamp,
  errorMessage,
  provider,
}: SyncStatusBadgeProps) {
  const [relativeTime, setRelativeTime] = useState('');

  useEffect(() => {
    const updateRelativeTime = () => {
      if (!lastSyncTimestamp) {
        setRelativeTime('Never');
        return;
      }

      const now = new Date();
      const diff = now.getTime() - new Date(lastSyncTimestamp).getTime();
      const seconds = Math.floor(diff / 1000);
      const minutes = Math.floor(seconds / 60);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (days > 0) {
        setRelativeTime(`${days} day${days !== 1 ? 's' : ''} ago`);
      } else if (hours > 0) {
        setRelativeTime(`${hours} hour${hours !== 1 ? 's' : ''} ago`);
      } else if (minutes > 0) {
        setRelativeTime(`${minutes} minute${minutes !== 1 ? 's' : ''} ago`);
      } else {
        setRelativeTime('Just now');
      }
    };

    updateRelativeTime();
    const interval = setInterval(updateRelativeTime, 1000);

    return () => clearInterval(interval);
  }, [lastSyncTimestamp]);

  const getStatusDisplay = () => {
    if (!status) {
      return {
        icon: <HourglassEmptyIcon className="fill-current size-4" />,
        text: getStatusConfig('sync', 'unknown').label,
        bgColor: 'bg-neutral-700/50',
        textColor: 'text-neutral-400',
      };
    }

    const config = getStatusConfig('sync', status.toLowerCase());

    switch (status.toLowerCase()) {
      case 'success':
        return {
          icon: <CheckCircleIcon className="fill-current size-4" />,
          text: config.label,
          bgColor: 'bg-green-900/50',
          textColor: 'text-green-400',
        };
      case 'error':
        return {
          icon: <ErrorIcon className="fill-current size-4" />,
          text: config.label,
          bgColor: 'bg-red-900/50',
          textColor: 'text-red-400',
        };
      case 'warning':
        return {
          icon: <WarningIcon className="fill-current size-4" />,
          text: config.label,
          bgColor: 'bg-yellow-900/50',
          textColor: 'text-yellow-400',
        };
      default:
        return {
          icon: <HourglassEmptyIcon className="fill-current size-4" />,
          text: config.label,
          bgColor: 'bg-neutral-700/50',
          textColor: 'text-neutral-400',
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          {statusDisplay.icon}
          <span className="text-sm text-neutral-300 capitalize">
            {provider}
          </span>
        </div>
        <div className="size-1 rounded-full bg-neutral-600" />
        <span className={`text-sm font-medium ${statusDisplay.textColor}`}>
          {statusDisplay.text}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-neutral-500">
        <span>{relativeTime}</span>
        {errorMessage && (
          <span
            className="text-red-400/80 hover:text-red-400 cursor-help transition-colors"
            title={errorMessage}
          >
            ({errorMessage.substring(0, 30)}
            {errorMessage.length > 30 ? '...' : ''})
          </span>
        )}
      </div>
    </div>
  );
}
